/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useEffect, useState } from "react";
import axiosWrapper from "../../services/axiosWrapper";
import { getBackendUrl } from "../../utils";
import { untranslated, useTranslation } from "@i18n";
import type { GetAllUsersInSpaceResponse, IncludedUsersOfProjectResponse } from "@ServerTypes/accounts/accounts.schema";
import type { UpdateModuleResourcePermissionResponse } from "@ServerTypes/permissions/permissions.schema";
import _ from "lodash";
import TextFieldComponent from "../../cubeComponent/Mui_Form/TextFieldComponent";
import UserNameTagComponent from "../../cubeComponent/html/UserNameTagComponent";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { BUTTON_TYPES } from "../../cubeComponent/constants/globalVariable";
import IconComponent from "../../cubeComponent/icons";
import { getRoleIdNameMapper, setModulePermissions } from "./constants";
import { folderRoles } from "@Helpers/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import Text from "@CubeComponents/html/Text/Text";

/**
 * One row of a module resource's embedded access roster, shared by all ten modules this component
 * serves. `role` and `permissions` are the numeric `folderRoles()` / `folderPermissions()` ids —
 * which is what every resource actually stores and what the server's Zod schemas declare (assets'
 * `assetAccessUserSchema` types `role` as `z.number()`).
 *
 * It was previously declared `role: string`, which is why nearly every caller reached this component
 * through an `as unknown as ComponentProps<…>["users"]` double cast: the cast was hiding this
 * mismatch rather than any problem at the call site.
 */
export type ModuleAccessUser = {
  id: string;
  role: number;
  permissions: Array<number>;
};

/** A user row from getAllUsersInSpace / includedUsersController, as this list uses it. */
type AccessUser = { id: string; fullName?: string };

/** The role chip shown next to each user; `frontendName` comes from the module's role-label map. */
type RoleValue = { frontendName?: unknown; id?: string | number };

type AccessUserRow = AccessUser & { value: RoleValue };

/** One selectable role, exactly as `setModulePermissions` builds it. */
type RoleOption = ReturnType<typeof setModulePermissions>[number];

type SpaceUsersAccessProps = {
  spaceId?: string;
  users: ModuleAccessUser[];
  projectId?: string;
  resourceId: string;
  module:
    | "CLASH_TEST"
    | "FORMS"
    | "TASKS"
    | "ISSUES"
    | "REVIEWS"
    | "MAILBOX"
    | "ASSETS"
    | "CONTRACTS"
    | "ESTIMATES"
    | "SCHEDULES";
  accessChangeCompleteCallback?: () => void;
  /**
   * Renders the roster read-only. The server is the authority (it refuses the write without
   * RESOURCE_CHANGE_PERMISSION on the resource); this only keeps the caller from being offered a
   * change that will be rejected. Defaults to editable so existing callers are unaffected.
   */
  disabled?: boolean;
};
const ModuleUsersAccess = ({
  spaceId,
  users = [],
  module,
  resourceId,
  projectId,
  accessChangeCompleteCallback,
  disabled = false,
}: SpaceUsersAccessProps) => {
  const { t } = useTranslation();
  const [allUsers, setAllUsers] = useState<AccessUserRow[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<AccessUserRow[]>([]);
  const [permissionsArray, setPermissionsArray] = useState<RoleOption[]>([]);
  const dispatch = useDispatch();

  const folderRolesData = folderRoles();

  const mapUserWithPermission = (usersList: Array<AccessUser>) => {
    const finalUsers: AccessUserRow[] = usersList.map((user: AccessUser) => {
      const value: RoleValue = {};
      const roleIdNameMapper = getRoleIdNameMapper(module);
      const userRole = users.find((u: ModuleAccessUser) => u.id == user.id);
      if (userRole) {
        value.frontendName = roleIdNameMapper[userRole.role];
        value.id = userRole.role;
      } else {
        value.frontendName = roleIdNameMapper[7];
        value.id = 7;
      }
      return {
        ...user,
        value,
      };
    });
    setAllUsers(finalUsers);
    setFilteredUsers(finalUsers);
  };

  const fetchAllSpaceUsers = async () => {
    const response = await axiosWrapper.post<GetAllUsersInSpaceResponse>(
      `${getBackendUrl()}/accounts/getAllUsersInSpace`,
      {
        spaceId,
      },
    );
    if (response.data.status) {
      mapUserWithPermission(response.data.result as AccessUser[]);
    }
  };

  const fetchAllProjectUsers = async () => {
    const query = { value: { projectId, source: "projectSetting" } };
    const response = await axiosWrapper.post<IncludedUsersOfProjectResponse>(
      `${getBackendUrl()}/accounts/includedUsersController`,
      query,
    );
    if (response.data.status) {
      mapUserWithPermission((response.data.result ?? []) as AccessUser[]);
    }
  };

  useEffect(() => {
    if (spaceId) {
      fetchAllSpaceUsers();
    } else if (projectId) {
      fetchAllProjectUsers();
    }
  }, [spaceId, projectId]);

  useEffect(() => {
    const res = setModulePermissions(module);
    setPermissionsArray(res);
  }, [module]);

  const handleSearchUsers: React.ComponentProps<typeof TextFieldComponent>["onChange"] = (args) => {
    const value = args.target.value?.toLowerCase() || "";
    setFilteredUsers(() => allUsers.filter((user: AccessUserRow) => user.fullName?.toLowerCase().includes(value)));
  };

  // `option` carries SelectDropDown's declared union; this dropdown is single-select, so every read
  // below narrows it to one RoleOption with an erased assertion.
  const additionalvalidations = ({ option, userId }: { option: RoleOption | RoleOption[] | null; userId: string }) => {
    if (module == "FORMS") {
      if (
        (option as RoleOption).id == folderRolesData.RESOURCE_ADMIN.id ||
        (option as RoleOption).id == folderRolesData.RESOURCE_EDITOR.id
      ) {
        dispatch(
          showToastNotification({
            content: t("This permission can only be updated through the corresponding form flow."),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return false;
      }
      const isUserHaveFullAccess = users.find(
        (user: ModuleAccessUser) => user.id == userId && [1, 2].includes(user.role),
      );
      if (isUserHaveFullAccess) {
        dispatch(
          showToastNotification({
            content: t("Permission for this user cannot be changed"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return false;
      }
    } else if (module === "REVIEWS") {
      const currentRole = users.find((user: ModuleAccessUser) => user.id == userId)?.role;
      if (
        (option as RoleOption).id == folderRolesData.RESOURCE_ADMIN.id &&
        currentRole !== folderRolesData.RESOURCE_EDITOR.id
      ) {
        dispatch(
          showToastNotification({
            content: t("Only can update permission users can be promoted to Full Access."),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return false;
      }
    }
    return true;
  };
  const setUserPermissionData = async ({
    option,
    userId,
  }: {
    option: RoleOption | RoleOption[] | null;
    userId: string;
  }) => {
    const res = additionalvalidations({ option, userId });
    if (!res) {
      return;
    }
    const payload = {
      role: (option as RoleOption).id,
      userId,
      resourceId,
      module,
    };
    const response = await axiosWrapper.post<UpdateModuleResourcePermissionResponse>(
      `${getBackendUrl()}/permissions/updateModuleResourcePermission`,
      payload,
    );
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("Successfully updated the permission"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      setAllUsers((prev: AccessUserRow[]) => {
        const newData = prev.map((user: AccessUserRow) => {
          if (user.id == userId) {
            user.value = {
              frontendName: (option as RoleOption).frontendName,
              id: (option as RoleOption).id,
            };
          }
          return user;
        });
        return newData;
      });
      accessChangeCompleteCallback?.();
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(response.data.errorMessage),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const itemTemplate = (data: RoleOption) => {
    return (
      <div className="flex gap-3 p-1 items-center">
        <div>
          <IconComponent name={data.name} width="1.5rem" height="1.5rem" />
        </div>
        <div className="flex flex-col">
          <div className="font-semibold">{data.frontendName}</div>
          <Text className="text-xs" id="qweqwe" sx={{ whiteSpace: "normal" }}>
            {data.description}
          </Text>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <TextFieldComponent onChange={handleSearchUsers} placeholder="Search User(s)" />
      </div>
      <div className="flex flex-col gap-5">
        {filteredUsers.map((user: AccessUserRow) => (
          <div key={user.id} className="flex">
            <div className="flex-1">{<UserNameTagComponent id={user.id} />} </div>
            <div className="flex-1 flex justify-end">
              <SelectDropDown
                id={`userRole_${user.id}`}
                disabled={disabled}
                renderOption={itemTemplate}
                onChange={(option) => setUserPermissionData({ option, userId: user.id })}
                // The chip state only carries the id + label, so it is asserted to the option type
                // the dropdown is generic over. Erased.
                value={user.value as RoleOption}
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                fields={{
                  text: "frontendName",
                  value: "id",
                }}
                options={permissionsArray}
                buttonWidth="9.375rem"
                popupAlign="right"
                popperWidth={400}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default ModuleUsersAccess;
